import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, Chip, Tooltip, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { bloomberg } from '../theme/bloombergTheme';
import {
  ModelPortfolioDetail, Scorecard, getScorecard, REBALANCE_SOURCE_LABELS, isActiveSection,
} from '../services/api';
import YardstickPicker from './YardstickPicker';
import WhyPanel from './WhyPanel';

/**
 * A model's place in the system.
 *  - Model Portfolios (theme bases, screens, allocations): its active versions
 *    and a button to create one.
 *  - Active Portfolios (active versions, convictions, curated themes, sector &
 *    regional baskets): how it is doing against its benchmark.
 *
 * Two comparisons, kept apart on purpose:
 *  - since the weights were set: both from that day on. Real, out of sample.
 *  - backtest: today's weights run over the past. Weights picked by looking
 *    at that same past (Max Sharpe especially) always look good here.
 */

const MONO = bloomberg.font.mono;
const eyebrow = { fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase' as const, color: bloomberg.text.muted };
const pct = (v?: number | null, sign = true) => (v === null || v === undefined ? '—'
  : `${sign ? (v >= 0 ? '+' : '−') : ''}${Math.abs(v * 100).toFixed(1)}%`);
const signed = (v?: number | null, dp = 2) => (v === null || v === undefined ? '—' : `${v >= 0 ? '+' : '−'}${Math.abs(v).toFixed(dp)}`);
const tone = (v?: number | null, better: 'up' | 'down' = 'up') => {
  if (v === null || v === undefined || Math.abs(v) < 1e-9) return bloomberg.text.secondary;
  return (better === 'up' ? v > 0 : v < 0) ? bloomberg.status.positive : bloomberg.status.negative;
};
const longDate = (d: string) => new Date(d.length === 10 ? `${d}T00:00:00` : d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
const ACC = '#a855f7';   // Active-section accent
const YARD = '#94a3b8';

function Spark({ series }: { series: { date: string; model: number; yardstick: number }[] }) {
  const w = 260, h = 64, pad = 4;
  const vals = series.flatMap((p) => [p.model, p.yardstick]);
  const lo = Math.min(...vals), hi = Math.max(...vals);
  const span = hi - lo || 1;
  const x = (i: number) => pad + (i / Math.max(1, series.length - 1)) * (w - 2 * pad);
  const y = (v: number) => h - pad - ((v - lo) / span) * (h - 2 * pad);
  const line = (k: 'model' | 'yardstick') => series.map((p, i) => `${i ? 'L' : 'M'}${x(i)},${y(p[k])}`).join(' ');
  return (
    <svg viewBox={`0 0 ${w} ${h}`} width="100%" height={h} role="img" aria-label="This portfolio and its benchmark since the start date">
      {lo < 100 && hi > 100 && <line x1={pad} x2={w - pad} y1={y(100)} y2={y(100)} stroke={bloomberg.border.secondary} strokeDasharray="3 3" />}
      <path d={line('yardstick')} fill="none" stroke={YARD} strokeWidth={1.6} />
      <path d={line('model')} fill="none" stroke={ACC} strokeWidth={2} />
    </svg>
  );
}

export default function ModelLineage({ portfolio, onOpen, onCompare, onCreate, creating }: {
  portfolio: ModelPortfolioDetail;
  onOpen: (id: number) => void;
  onCompare: (modelId: number) => void;
  onCreate: () => void;
  creating: boolean;
}) {
  const [sc, setSc] = useState<Scorecard | null>(null);
  const [scError, setScError] = useState(false);
  const [picking, setPicking] = useState(false);
  const navigate = useNavigate();
  const scored = isActiveSection(portfolio.kind);

  useEffect(() => {
    setSc(null); setScError(false);
    if (!scored) return;
    getScorecard(portfolio.id).then(setSc).catch(() => setScError(true));
  }, [portfolio.id, portfolio.updated_at, scored]);

  // ── Model Portfolios: its active versions ──
  if (!scored) {
    const kids = (portfolio.children || []).filter((c) => c.kind === 'active');
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
        <Typography sx={{ fontSize: 12, color: bloomberg.text.muted }}>Active versions</Typography>
        {kids.length === 0 && <Typography sx={{ fontSize: 12, color: bloomberg.text.muted }}>none yet</Typography>}
        {kids.map((k) => (
          <Chip key={k.id} size="small" label={k.name} onClick={() => onOpen(k.id)}
            sx={{ fontSize: 12, bgcolor: `${ACC}1f`, color: bloomberg.text.primary, border: `1px solid ${ACC}66`, '&:hover': { bgcolor: `${ACC}33` } }} />
        ))}
        <Tooltip title={`A copy of this model you can re-weight — by hand or with Efficient Frontier weights. ${portfolio.kind === 'theme' ? 'The equal-weight base' : 'This model'} stays exactly as it is, so the two can be compared.`}>
          <span>
            <Button size="small" onClick={onCreate} disabled={creating}
              sx={{ textTransform: 'none', fontSize: 12, border: `1px dashed ${bloomberg.border.secondary}`, px: 1.25, py: 0.1 }}>
              {creating ? 'Creating…' : '+ Create active version'}
            </Button>
          </span>
        </Tooltip>
      </Box>
    );
  }

  // ── Active Portfolios: against its benchmark ──
  const ys = sc?.yardstick;
  const since = sc?.since_change;
  const live = !!since && since.days >= 1;
  const diff = live ? since!.model_return - since!.yardstick_return : null;
  const m = sc?.backtest.model || {}, b = sc?.backtest.yardstick || {};
  const rows: { k: string; mv?: number | null; bv?: number | null; fmt: (v?: number | null) => string; dfmt: (v?: number | null) => string; better?: 'up' | 'down' }[] = [
    { k: '3M return', mv: m.returns?.['3M'], bv: b.returns?.['3M'], fmt: (v) => pct(v), dfmt: (v) => pct(v) },
    { k: '1Y return', mv: m.returns?.['1Y'], bv: b.returns?.['1Y'], fmt: (v) => pct(v), dfmt: (v) => pct(v) },
    { k: 'Volatility', mv: m.vol, bv: b.vol, fmt: (v) => pct(v, false), dfmt: (v) => pct(v), better: 'down' },
    { k: 'Sharpe (1Y)', mv: m.sharpe, bv: b.sharpe, fmt: (v) => (v === null || v === undefined ? '—' : v.toFixed(2)), dfmt: (v) => signed(v) },
  ];
  const d = (a?: number | null, c?: number | null) => (a === null || a === undefined || c === null || c === undefined ? null : a - c);
  const last = sc?.last_change;
  const isVersion = portfolio.kind === 'active' && !!portfolio.parent_id;
  const ysShort = 'Benchmark';

  return (
    <Box sx={{ mb: 1.5, border: `1px solid ${bloomberg.border.primary}`, borderTop: `3px solid ${ACC}`, borderRadius: 1.5, bgcolor: bloomberg.bg.secondary, p: 1.75 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flexWrap: 'wrap', mb: 1.25 }}>
        <Box sx={{ flex: 1, minWidth: 240 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography sx={{ ...eyebrow, color: ACC }}>{isVersion ? 'Active version · judged against' : 'Judged against'}</Typography>
            <Button onClick={() => navigate('/portfolio/active')} sx={{ p: 0, minWidth: 0, textTransform: 'none', fontSize: 11, color: bloomberg.text.muted, '&:hover': { color: bloomberg.text.primary, bgcolor: 'transparent' } }}>
              · all active portfolios
            </Button>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
            {ys?.kind === 'model' && ys.id ? (
              <Tooltip title={ys.full || ''}>
                <Button onClick={() => onOpen(ys.id!)} sx={{ p: 0, minWidth: 0, textTransform: 'none', fontSize: 15, fontWeight: 700, color: bloomberg.text.primary, '&:hover': { textDecoration: 'underline', bgcolor: 'transparent' } }}>
                  {ys.label} →
                </Button>
              </Tooltip>
            ) : (
              <Tooltip title={ys?.full || ''}>
                <Typography sx={{ fontSize: 15, fontWeight: 700, color: ys ? bloomberg.text.primary : bloomberg.text.muted }}>
                  {ys ? ys.label : sc ? 'No benchmark yet' : '…'}
                </Typography>
              </Tooltip>
            )}
            <Button size="small" onClick={() => setPicking(true)} sx={{ textTransform: 'none', fontSize: 11.5, minWidth: 0, px: 0.75, py: 0 }}>
              {ys ? 'Change' : 'Choose one'}
            </Button>
          </Box>
          {sc && (
            <Typography sx={{ fontSize: 12, color: bloomberg.text.muted, mt: 0.25 }}>
              {last
                ? <>Weights set {longDate(last.at)} · {REBALANCE_SOURCE_LABELS[last.source] || last.source}{last.note ? ` · ${last.note}` : ''}</>
                : <>Tracked from creation, {sc.start.date ? longDate(sc.start.date) : '—'} · approximate: the weight log began in Aug 2026, so earlier edits aren't recorded</>}
            </Typography>
          )}
        </Box>
        {ys?.kind === 'model' && ys.id && (
          <Button size="small" variant="outlined" onClick={() => onCompare(ys.id!)} sx={{ textTransform: 'none' }}>
            Compare in detail
          </Button>
        )}
      </Box>

      {!sc && !scError && <CircularProgress size={18} />}
      {scError && <Typography sx={{ fontSize: 12.5, color: bloomberg.text.muted }}>Scorecard unavailable right now.</Typography>}
      {sc && ys && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) minmax(0, 1.2fr)' }, gap: 2 }}>
          {/* Since the start: real */}
          <Box sx={{ bgcolor: bloomberg.bg.primary, borderRadius: 1, p: 1.5, border: `1px solid ${bloomberg.border.primary}` }}>
            <Typography sx={eyebrow}>
              Since {sc.start.date ? longDate(sc.start.date) : 'the start'} · {sc.start.test ? 'test: backdated, not a real record' : sc.start.walk_forward ? 'walk-forward: weights from prices to that date only' : sc.start.exact ? 'real' : 'real, approx.'}
            </Typography>
            {live ? (
              <>
                <Box sx={{ display: 'flex', gap: 2.5, mt: 0.75, alignItems: 'baseline', flexWrap: 'wrap' }}>
                  <Box>
                    <Typography sx={{ fontSize: 11, color: ACC, fontWeight: 600 }}>This portfolio</Typography>
                    <Typography sx={{ fontFamily: MONO, fontSize: 18, fontWeight: 700 }}>{pct(since!.model_return)}</Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 11, color: YARD, fontWeight: 600 }}>{ysShort}</Typography>
                    <Typography sx={{ fontFamily: MONO, fontSize: 18, fontWeight: 700 }}>{pct(since!.yardstick_return)}</Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 11, color: bloomberg.text.muted, fontWeight: 600 }}>Difference</Typography>
                    <Typography sx={{ fontFamily: MONO, fontSize: 18, fontWeight: 700, color: tone(diff) }}>{pct(diff)}</Typography>
                  </Box>
                </Box>
                {since!.series.length > 2 && <Box sx={{ mt: 1 }}><Spark series={since!.series} /></Box>}
                <Typography sx={{ fontSize: 11.5, color: bloomberg.text.muted, mt: 0.5 }}>
                  Over {since!.days} trading day{since!.days > 1 ? 's' : ''}.
                  {since!.days < 20 ? ' Too early to judge: give it a few weeks.' : ''}
                </Typography>
              </>
            ) : (
              <Typography sx={{ fontSize: 12.5, color: bloomberg.text.secondary, mt: 0.75, lineHeight: 1.55 }}>
                {since ? 'The weights were set today, so there is no real track record yet. From the next close this shows how the portfolio is doing against its benchmark.'
                  : 'Not enough stored prices to compare over this period yet.'}
              </Typography>
            )}
          </Box>

          {/* Backtest: in-sample */}
          <Box sx={{ bgcolor: bloomberg.bg.primary, borderRadius: 1, p: 1.5, border: `1px solid ${bloomberg.border.primary}` }}>
            <Typography sx={eyebrow}>Backtest with today's weights · in-sample</Typography>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 6, fontSize: 12.5 }}>
              <thead>
                <tr style={{ color: bloomberg.text.muted, fontSize: 11 }}>
                  <th scope="col" style={{ textAlign: 'left', fontWeight: 600, padding: '3px 0' }} />
                  <th scope="col" style={{ textAlign: 'right', fontWeight: 600, color: ACC }}>Portfolio</th>
                  <th scope="col" style={{ textAlign: 'right', fontWeight: 600, color: YARD }}>{ysShort}</th>
                  <th scope="col" style={{ textAlign: 'right', fontWeight: 600 }}>Δ</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const dv = d(r.mv, r.bv);
                  return (
                    <tr key={r.k} style={{ borderTop: `1px solid ${bloomberg.border.primary}` }}>
                      <th scope="row" style={{ textAlign: 'left', fontWeight: 500, color: bloomberg.text.secondary, padding: '4px 0' }}>{r.k}</th>
                      <td style={{ textAlign: 'right', fontFamily: MONO }}>{r.fmt(r.mv)}</td>
                      <td style={{ textAlign: 'right', fontFamily: MONO, color: bloomberg.text.secondary }}>{r.fmt(r.bv)}</td>
                      <td style={{ textAlign: 'right', fontFamily: MONO, fontWeight: 600, color: tone(dv, r.better) }}>{r.dfmt(dv)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <Typography sx={{ fontSize: 11.5, color: bloomberg.text.muted, mt: 0.75, lineHeight: 1.5 }}>
              Today's weights run over past prices. Weights chosen by looking at that same past (Max Sharpe
              especially) always look good here, so trust the real figures on the left.
            </Typography>
          </Box>
        </Box>
      )}

      {sc && ys && <WhyPanel portfolioId={portfolio.id} updatedAt={`${portfolio.updated_at}|${ys.ref}`} />}

      <YardstickPicker open={picking} portfolioId={portfolio.id} portfolioName={portfolio.name}
        current={ys?.ref ?? null} onClose={() => setPicking(false)} onSaved={setSc} />
    </Box>
  );
}
